import {
  GridRenderCellParams,
  GridValueFormatterParams,
} from '@mui/x-data-grid'
import { TFunction } from 'i18next'
import React from 'react'
import { InvitationSource, PotentialInviteStatus } from '@swagger'
import UserAvatar from '../Elements/UserAvatar'
import { ActionableTableColumn } from '../Table/ActionableTable'

import { PotentialInviteItem } from '@swagger'
import { maskEmail } from '@utils/maskEmail'

export interface AwaitingDecisionRow {
  id: number
  familyName: string
  source: InvitationSource
  contactEmail: string
  status: PotentialInviteStatus
  crmEmails?: string[]
  potentialInvite: PotentialInviteItem
  parentFirstName: string
  parentLastName: string
  potentialInviteKey: number
  parentUserKey?: number
  previousPrograms?: PotentialInviteItem['previousPrograms']
}

export const getSourceLabel = (
  t: TFunction,
  source?: InvitationSource
): string => {
  switch (source) {
    case InvitationSource.LcdReferral:
      return t(
        'Communities.AwaitingDecisionsTable.Source.LCDReferral',
        'LCD Referral'
      )
    case InvitationSource.ReturningFamily:
      return t(
        'Communities.AwaitingDecisionsTable.Source.ReturningFamily',
        'Returning Family'
      )
    default:
      return t(
        'Communities.AwaitingDecisionsTable.Source.AllSources',
        'All Sources'
      )
  }
}

export const getStatusLabel = (
  t: TFunction,
  status?: PotentialInviteStatus
): string => {
  switch (status) {
    case PotentialInviteStatus.Declined:
      return t('Communities.AwaitingDecisionsTable.Status.Declined', 'Pass')
    case PotentialInviteStatus.Pending:
      return t(
        'Communities.AwaitingDecisionsTable.Status.AwaitingDecision',
        'Awaiting Decision'
      )
    default:
      return t(
        'Communities.AwaitingDecisionsTable.Status.AllStatuses',
        'All Statuses'
      )
  }
}

export const getAwaitingDecisionsTableColumns = (
  t: TFunction
): ActionableTableColumn[] => [
  {
    fieldName: 'avatar',
    columnHeaderName: '',
    renderCell: ({ row }: GridRenderCellParams<AwaitingDecisionRow>) => (
      <UserAvatar familyName={row.familyName} />
    ),
    maxWidth: 75,
  },
  {
    fieldName: 'familyName',
    columnHeaderName: t(
      'Communities.AwaitingDecisionsTable.Header.FamilyName',
      'Family Name'
    ),
    sortable: true,
  },
  {
    fieldName: 'source',
    columnHeaderName: t(
      'Communities.AwaitingDecisionsTable.Header.Source',
      'Source'
    ),
    sortable: true,
    valueFormatter: (params: GridValueFormatterParams<InvitationSource>) =>
      getSourceLabel(t, params.value),
  },
  {
    fieldName: 'contactEmail',
    columnHeaderName: t(
      'Communities.AwaitingDecisionsTable.Header.Email',
      'Email'
    ),
    renderCell: ({ row }: GridRenderCellParams<AwaitingDecisionRow>) => {
      const isReturningFamily = row.source === InvitationSource.ReturningFamily
      const emailToDisplay = isReturningFamily
        ? maskEmail(row.contactEmail)
        : row.contactEmail

      return <span title={emailToDisplay}>{emailToDisplay}</span>
    },
  },
  {
    fieldName: 'status',
    columnHeaderName: t(
      'Communities.AwaitingDecisionsTable.Header.Status',
      'Status'
    ),
    sortable: true,
    valueFormatter: (params: GridValueFormatterParams<PotentialInviteStatus>) =>
      getStatusLabel(t, params.value),
  },
]
